import { useEffect, useState } from 'react';
import { createRouterSelector } from '@atlassian/react-resource-router';
import {
	useAnalyticsEvents,
	fireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';

const HYDRATION_STATS = {
	enabled: false,
	hasFatalError: false,
	warningCount: 0,
};

const useRouteName = createRouterSelector((state) => state.route.name);

/**
 * Reports the overall success / failure of React hydration once complete. This is inside a React component so that it
 * can access analytics context and the current route.
 */
export const HydrationStatsReporter = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const initialRouteName = useRouteName();
	// Using state here because we want to capture the route name on initial render, and not have it change when the effect runs
	const [routeName] = useState(initialRouteName);

	useEffect(() => {
		if (!HYDRATION_STATS.enabled) {
			return;
		}
		HYDRATION_STATS.enabled = false;

		let cancelled = false;
		// Wait until React hydration is likely done
		setTimeout(() => {
			requestIdleCallback(() => {
				if (cancelled) {
					return;
				}
				const { hasFatalError, warningCount } = HYDRATION_STATS;
				fireOperationalAnalytics(
					createAnalyticsEvent({}),
					!hasFatalError ? 'reactHydrate success' : 'reactHydrate failure',
					{
						warningCount: bucketedWarningCount(warningCount),
						routeName,
					},
				);
				log.safeInfoWithoutCustomerData(
					'react-hydration-reporting',
					!hasFatalError ? 'Hydration success' : 'Hydration failure',
					{
						warningCount,
						routeName,
					},
				);
			});
		}, 10_000);
		return () => {
			cancelled = true;
		};
	}, [createAnalyticsEvent, routeName]);

	return null;
};

export const bucketedWarningCount = (warningCount: number): string => {
	if (warningCount <= 9) {
		return warningCount.toString();
	}
	return '10+';
};

/**
 * Enables high-level hydration stats based on hydration error logs.
 * This runs in prod builds, where React isn't patched with detailed hydration errors.
 */
export const initProdHydrationStatReporting = () => {
	HYDRATION_STATS.enabled = true;
};

export const addErrorForHydrationStats = (error: Error) => {
	switch (classifyAsHydrationError(error)) {
		case HYDRATION_FATAL_ERROR:
			HYDRATION_STATS.hasFatalError = true;
			break;
		case HYDRATION_WARNING:
			HYDRATION_STATS.warningCount++;
			break;
		default:
			break;
	}
};

const HYDRATION_FATAL_ERROR = 'fatal';
const HYDRATION_WARNING = 'warning';
type HydrationErrorType = typeof HYDRATION_FATAL_ERROR | typeof HYDRATION_WARNING;
/**
 * Determines if the error is a hydration error and if so, what type.
 * This is fragile because it depends on the exact React version's error messages (18.2.0), defined here:
 * https://github.com/facebook/react/blob/v18.2.0/scripts/error-codes/codes.json
 * To do this properly requires patching React (again). Using best-effort detection like this is the less-bad option.
 */
export const classifyAsHydrationError = ({ message }: Error): HydrationErrorType | null => {
	if (FATAL_MESSAGES.some((fatal) => message.includes(fatal))) {
		return HYDRATION_FATAL_ERROR;
	}
	if (WARNING_MESSAGES.some((warning) => message.includes(warning))) {
		return HYDRATION_WARNING;
	}
	return null;
};

const FATAL_MESSAGES = [407, 419, 421, 422, 423, 424].map(
	(code) => `Minified React error #${code}`,
);
const WARNING_MESSAGES = [418, 425].map((code) => `Minified React error #${code}`);
